/**
 * TargetFrameHUD - Premium DAoC / MMORPG-style top-center target unit frame.
 * Displays target's Name, Role/Rank, Distance, Big Animated HP Bar, SP/MP, Casting Bar, and CC badges.
 * Built with cached DOM nodes for zero-garbage-collection 60FPS performance.
 */

import { EntityState } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';

export class TargetFrameHUD {
  private container: HTMLElement | null = null;
  private isVisible = false;
  private currentTargetId: string | null = null;
  private currentTargetEntity: EntityState | null = null;
  private onToggleStick?: () => void;
  private onInviteParty?: (targetName: string) => void;
  private onTradeTarget?: (target: EntityState) => void;

  // Cached DOM elements
  private avatarEl!: HTMLElement;
  private nameEl!: HTMLElement;
  private jobEl!: HTMLElement;
  private distEl!: HTMLElement;
  private stickBtnEl!: HTMLElement;
  private inviteBtnEl!: HTMLElement;
  private tradeBtnEl!: HTMLElement;
  private hpBarFillEl!: HTMLElement;
  private hpTextEl!: HTMLElement;
  private mpBarContainerEl!: HTMLElement;
  private mpBarFillEl!: HTMLElement;
  private castContainerEl!: HTMLElement;
  private castBarFillEl!: HTMLElement;
  private castNameEl!: HTMLElement;
  private ccBadgeEl!: HTMLElement;

  constructor(
    onToggleStick?: () => void,
    onInviteParty?: (targetName: string) => void,
    onTradeTarget?: (target: EntityState) => void
  ) {
    this.onToggleStick = onToggleStick;
    this.onInviteParty = onInviteParty;
    this.onTradeTarget = onTradeTarget;
  }

  public mount(parent: HTMLElement): void {
    const el = document.createElement('div');
    el.id = 'target-frame-hud';
    el.style.position = 'absolute';
    el.style.top = '52px';
    el.style.left = '50%';
    el.style.transform = 'translateX(-50%)';
    el.style.width = '360px';
    el.style.background = 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.92) 100%)';
    el.style.border = '1.5px solid #475569';
    el.style.borderRadius = '8px';
    el.style.padding = '8px 12px';
    el.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.6), 0 0 12px rgba(245, 158, 11, 0.15)';
    el.style.zIndex = '90';
    el.style.userSelect = 'none';
    el.style.display = 'none';
    el.style.fontFamily = 'system-ui, -apple-system, sans-serif';
    el.style.transition = 'opacity 0.15s ease-out, transform 0.15s ease-out';

    // Internal DOM structure
    el.innerHTML = `
      <!-- Header: Avatar, Name, Job, Distance, Stick Button -->
      <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:6px;">
        <div style="display:flex; align-items:center; gap:8px;">
          <div id="tf-avatar" style="font-size:20px; width:28px; height:28px; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,0.4); border-radius:4px; border:1px solid #64748b;">🎯</div>
          <div>
            <div id="tf-name" style="font-size:13px; font-weight:bold; color:#f8fafc; text-shadow:0 1px 2px #000;">대상 이름</div>
            <div id="tf-job" style="font-size:10px; color:#94a3b8;">정예 괴수</div>
          </div>
        </div>
        <div style="display:flex; align-items:center; gap:6px;">
          <button id="tf-stick-btn" style="
            background: rgba(30, 41, 59, 0.85);
            border: 1px solid #475569;
            color: #cbd5e1;
            font-size: 10px;
            font-weight: bold;
            padding: 3px 8px;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.15s ease;
            display: flex;
            align-items: center;
            gap: 3px;
          " title="타겟 자동 따라가기 토글 (단축키: F)">
            🚶 따라가기 [F]
          </button>
          <button id="tf-invite-btn" style="
            display: none;
            background: rgba(29, 78, 216, 0.85);
            border: 1px solid #3b82f6;
            color: #ffffff;
            font-size: 10px;
            font-weight: bold;
            padding: 3px 8px;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.15s ease;
            align-items: center;
            gap: 3px;
          " title="대상 파티 초대">
            👥 파티초대
          </button>
          <button id="tf-trade-btn" style="
            display: none;
            background: rgba(180, 83, 9, 0.85);
            border: 1px solid #f59e0b;
            color: #ffffff;
            font-size: 10px;
            font-weight: bold;
            padding: 3px 8px;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.15s ease;
            align-items: center;
            gap: 3px;
          " title="대상과 1:1 교환 거래">
            🤝 거래
          </button>
          <div id="tf-dist" style="font-size:11px; font-weight:bold; color:#38bdf8; background:rgba(0,0,0,0.5); padding:2px 6px; border-radius:4px; border:1px solid #0369a1;">🏹 0.0m</div>
          <div id="tf-cc-badge" style="display:none; font-size:10px; font-weight:bold; color:#fef08a; background:#854d0e; padding:2px 6px; border-radius:4px; border:1px solid #eab308;">⚡ STUN</div>
        </div>
      </div>

      <!-- Big Primary HP Bar -->
      <div style="position:relative; width:100%; height:16px; background:#0f172a; border-radius:4px; overflow:hidden; border:1px solid #334155;">
        <div id="tf-hp-fill" style="width:100%; height:100%; background:linear-gradient(90deg, #dc2626, #ef4444); transition:width 0.1s linear;"></div>
        <div id="tf-hp-text" style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:bold; color:#ffffff; text-shadow:0 1px 3px #000;">
          100 / 100 (100%)
        </div>
      </div>

      <!-- Secondary MP Bar (Optional) -->
      <div id="tf-mp-container" style="width:100%; height:4px; background:#0f172a; border-radius:2px; overflow:hidden; margin-top:3px; border:1px solid #1e293b; display:none;">
        <div id="tf-mp-fill" style="width:100%; height:100%; background:linear-gradient(90deg, #2563eb, #38bdf8);"></div>
      </div>

      <!-- Target Casting Bar (Optional) -->
      <div id="tf-cast-container" style="display:none; width:100%; margin-top:5px; background:rgba(0,0,0,0.6); padding:3px 6px; border-radius:4px; border:1px solid #0284c7;">
        <div style="display:flex; justify-content:space-between; font-size:9px; font-weight:bold; color:#38bdf8; margin-bottom:2px;">
          <span id="tf-cast-name">주문 시전 중...</span>
          <span>주문 시전</span>
        </div>
        <div style="position:relative; width:100%; height:6px; background:#0f172a; border-radius:3px; overflow:hidden;">
          <div id="tf-cast-fill" style="width:0%; height:100%; background:linear-gradient(90deg, #0284c7, #38bdf8);"></div>
        </div>
      </div>
    `;

    parent.appendChild(el);
    this.container = el;

    // Cache elements
    this.avatarEl = el.querySelector('#tf-avatar') as HTMLElement;
    this.nameEl = el.querySelector('#tf-name') as HTMLElement;
    this.jobEl = el.querySelector('#tf-job') as HTMLElement;
    this.distEl = el.querySelector('#tf-dist') as HTMLElement;
    this.stickBtnEl = el.querySelector('#tf-stick-btn') as HTMLElement;
    this.inviteBtnEl = el.querySelector('#tf-invite-btn') as HTMLElement;
    this.tradeBtnEl = el.querySelector('#tf-trade-btn') as HTMLElement;
    this.hpBarFillEl = el.querySelector('#tf-hp-fill') as HTMLElement;
    this.hpTextEl = el.querySelector('#tf-hp-text') as HTMLElement;
    this.mpBarContainerEl = el.querySelector('#tf-mp-container') as HTMLElement;
    this.mpBarFillEl = el.querySelector('#tf-mp-fill') as HTMLElement;
    this.castContainerEl = el.querySelector('#tf-cast-container') as HTMLElement;
    this.castBarFillEl = el.querySelector('#tf-cast-fill') as HTMLElement;
    this.castNameEl = el.querySelector('#tf-cast-name') as HTMLElement;
    this.ccBadgeEl = el.querySelector('#tf-cc-badge') as HTMLElement;

    // Stick button click listener
    this.stickBtnEl.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.onToggleStick) {
        this.onToggleStick();
      }
    });

    // Party Invite button click listener
    this.inviteBtnEl?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.onInviteParty && this.nameEl) {
        const targetName = this.nameEl.innerText.trim();
        if (targetName) {
          this.onInviteParty(targetName);
        }
      }
    });

    // Trade button click listener
    this.tradeBtnEl?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.onTradeTarget && this.currentTargetEntity) {
        this.onTradeTarget(this.currentTargetEntity);
      }
    });
  }

  public update(player: EntityState | undefined, registry: EntityRegistry, isStick = false): void {
    if (!this.container) return;

    if (!player || !player.targetId) {
      if (this.isVisible) {
        this.container.style.display = 'none';
        this.isVisible = false;
        this.currentTargetId = null;
        this.currentTargetEntity = null;
      }
      return;
    }

    const target = registry.get(player.targetId);
    if (!target || target.isDead) {
      if (this.isVisible) {
        this.container.style.display = 'none';
        this.isVisible = false;
        this.currentTargetId = null;
        this.currentTargetEntity = null;
      }
      return;
    }

    this.currentTargetEntity = target;

    // Show container
    if (!this.isVisible) {
      this.container.style.display = 'block';
      this.isVisible = true;
    }

    // Update Stick Button UI
    if (this.stickBtnEl) {
      if (isStick) {
        this.stickBtnEl.style.background = 'linear-gradient(90deg, #15803d, #22c55e)';
        this.stickBtnEl.style.borderColor = '#4ade80';
        this.stickBtnEl.style.color = '#ffffff';
        this.stickBtnEl.style.boxShadow = '0 0 8px rgba(34, 197, 94, 0.5)';
        this.stickBtnEl.innerHTML = '🏃 따라가는 중 [F]';
      } else {
        this.stickBtnEl.style.background = 'rgba(30, 41, 59, 0.85)';
        this.stickBtnEl.style.borderColor = '#475569';
        this.stickBtnEl.style.color = '#cbd5e1';
        this.stickBtnEl.style.boxShadow = 'none';
        this.stickBtnEl.innerHTML = '🚶 따라가기 [F]';
      }
    }

    // Border and color tone based on category
    const isMonster = target.category === 'monster';
    const isResource = target.category === 'resource';

    if (this.currentTargetId !== target.id) {
      this.currentTargetId = target.id;
      if (isResource) {
        this.container.style.borderColor = '#f59e0b';
        this.container.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.6), 0 0 14px rgba(245, 158, 11, 0.4)';
        this.avatarEl.innerText = target.avatarIcon || '🌲';
        this.nameEl.innerText = target.name;
        this.jobEl.innerText = `${target.job} (좌클릭 채집)`;
        this.jobEl.style.color = '#fef08a';
        this.hpBarFillEl.style.background = 'linear-gradient(90deg, #d97706, #f59e0b)';
      } else if (isMonster) {
        this.container.style.borderColor = '#ef4444';
        this.container.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.6), 0 0 14px rgba(239, 68, 68, 0.35)';
        this.avatarEl.innerText = target.avatarIcon || '👹';
        this.nameEl.innerText = target.name;
        this.jobEl.innerText = `${target.job} (적군)`;
        this.jobEl.style.color = '#fca5a5';
        this.hpBarFillEl.style.background = 'linear-gradient(90deg, #b91c1c, #ef4444)';
      } else {
        this.container.style.borderColor = '#10b981';
        this.container.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.6), 0 0 14px rgba(16, 185, 129, 0.35)';
        this.avatarEl.innerText = target.avatarIcon || '🛡️';
        this.nameEl.innerText = target.name;
        this.jobEl.innerText = `${target.job} (아군)`;
        this.jobEl.style.color = '#86efac';
        this.hpBarFillEl.style.background = 'linear-gradient(90deg, #15803d, #22c55e)';
      }

      // 아군 플레이어/동료/NPC인 경우 거래 및 파티초대 버튼 노출
      if (this.inviteBtnEl) {
        const canInvite = !isMonster && !isResource && (target.category === 'player' || target.category === 'companion') && target.id !== player.id;
        this.inviteBtnEl.style.display = canInvite ? 'flex' : 'none';
      }

      if (this.tradeBtnEl) {
        const canTrade = !isMonster && !isResource && target.id !== player.id;
        this.tradeBtnEl.style.display = canTrade ? 'flex' : 'none';
      }
    }

    // Distance calculation
    const dist = registry.getDistance(player, target);
    if (isResource) {
      const canHarvest = dist <= 2.5;
      this.distEl.innerText = `🏹 ${dist.toFixed(1)}m ${canHarvest ? '✔ 채집가능' : '✖ 접근필요'}`;
      this.distEl.style.color = canHarvest ? '#4ade80' : '#f87171';
      this.distEl.style.borderColor = canHarvest ? '#15803d' : '#991b1b';
    } else {
      this.distEl.innerText = `🏹 ${dist.toFixed(1)}m`;
      this.distEl.style.color = '#38bdf8';
      this.distEl.style.borderColor = '#0369a1';
    }

    // HP Bar & Durability / Respawn Countdown
    if (isResource && (target.isDead || target.resources.hp <= 0)) {
      const remaining = target.respawnRemaining || 0;
      const total = target.respawnTotal || 8.0;
      const respawnPct = Math.max(0, Math.min(1, 1 - remaining / total));
      this.hpBarFillEl.style.width = `${(respawnPct * 100).toFixed(1)}%`;
      this.hpBarFillEl.style.background = 'linear-gradient(90deg, #059669, #10b981)';
      this.hpTextEl.innerText = `⏳ 자라나는 중... (${remaining.toFixed(1)}초)`;
    } else {
      const hp = Math.max(0, target.resources.hp);
      const maxHp = target.resources.maxHp;
      const hpPct = Math.max(0, Math.min(1, hp / maxHp));
      this.hpBarFillEl.style.width = `${(hpPct * 100).toFixed(1)}%`;
      if (isResource) {
        this.hpBarFillEl.style.background = 'linear-gradient(90deg, #d97706, #f59e0b)';
        this.hpTextEl.innerText = `내구도: ${Math.round(hp)} / ${maxHp} (${Math.round(hpPct * 100)}%)`;
      } else {
        this.hpTextEl.innerText = `${Math.round(hp)} / ${maxHp} (${Math.round(hpPct * 100)}%)`;
      }
    }

    // MP bar if target has MP
    if (target.resources.maxMp > 0) {
      this.mpBarContainerEl.style.display = 'block';
      const mpPct = Math.max(0, Math.min(1, target.resources.mp / target.resources.maxMp));
      this.mpBarFillEl.style.width = `${(mpPct * 100).toFixed(1)}%`;
    } else {
      this.mpBarContainerEl.style.display = 'none';
    }

    // CC Badge
    if (target.cc) {
      this.ccBadgeEl.style.display = 'block';
      let ccText = '⚡ STUN';
      let ccBg = '#854d0e';
      let ccBorder = '#eab308';
      if (target.cc.type === 'mez') {
        ccText = '💤 MEZ';
        ccBg = '#581c87';
        ccBorder = '#c084fc';
      } else if (target.cc.type === 'root') {
        ccText = '🕸️ ROOT';
        ccBg = '#14532d';
        ccBorder = '#4ade80';
      }
      this.ccBadgeEl.style.background = ccBg;
      this.ccBadgeEl.style.borderColor = ccBorder;
      this.ccBadgeEl.innerText = `${ccText} ${target.cc.remaining.toFixed(1)}s`;
    } else {
      this.ccBadgeEl.style.display = 'none';
    }

    // Casting Bar
    if (target.cast) {
      this.castContainerEl.style.display = 'block';
      const castPct = Math.min(1, target.cast.progress / target.cast.castTime);
      this.castBarFillEl.style.width = `${(castPct * 100).toFixed(1)}%`;
      this.castNameEl.innerText = `⏳ ${target.cast.skillName}`;
    } else {
      this.castContainerEl.style.display = 'none';
    }
  }
}
